import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { cloneListOfCloneable, DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { MonthOfYear } from '../month-of-year.js';
import { Price } from '../price.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { DayOfYear } from '../day-of-year.js';
import { Currency } from '../currency.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { StatisticsDiscountUtils } from '../../../utility/statistics_discount_utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { DailySalesIncomeSummary } from './daily-sales-income-summary.js';
import { TicketTemplateSalesIncomeSummary } from './ticket-template-sales-income-summary.js';
import { TicketOrderStatus } from '../ticket-order-status.js';
import { EventTicketStatus } from '../event-ticket.js';
import { LegacyPaymentHelper } from '../../../payment/legacy-payment-helper.js';

export class MonthlySalesIncomeSummary extends DataModel {
  static readonly TYPE_ID: string = '719c6b49327949dfa9384fafb7743e1b';

  constructor(
    public readonly monthOfYear: MonthOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly currency: Currency,
    public readonly dailySales: DailySalesIncomeSummary[],
    public readonly ticketTemplateSales: TicketTemplateSalesIncomeSummary[],
  ) {
    super();
  }

  static empty(monthOfYear: MonthOfYear, timezone: TimeZoneRegion, currency: Currency): MonthlySalesIncomeSummary {
    return new MonthlySalesIncomeSummary(
        monthOfYear,
        timezone,
        currency,
        new Array<DailySalesIncomeSummary>(),
        new Array<TicketTemplateSalesIncomeSummary>(),
    );
  }

  get $typeId(): string {
    return MonthlySalesIncomeSummary.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): MonthlySalesIncomeSummary {
    return new MonthlySalesIncomeSummary(
        context.fromData<MonthOfYear>(MapUtils.get<SerializedData>(map, 'monthOfYear')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, 'currency'), Currency),
        MapUtils.getList<DailySalesIncomeSummary>(map, 'dailySales', (data) => DailySalesIncomeSummary.fromMap(context, data as SerializedData)),
        MapUtils.getList<TicketTemplateSalesIncomeSummary>(map, 'ticketTemplateSales', (data) => TicketTemplateSalesIncomeSummary.fromMap(context, data as SerializedData)),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'monthOfYear': context.toData(this.monthOfYear),
      'timezone': fromEnumAsString(this.timezone),
      'currency': fromEnumAsString(this.currency),
      'dailySales': this.dailySales.map((e) => e.toMap(context)),
      'ticketTemplateSales': this.ticketTemplateSales.map((e) => e.toMap(context)),
    };
  }

  clone(): this {
    return new MonthlySalesIncomeSummary(
        this.monthOfYear.clone(),
        this.timezone,
        this.currency,
        cloneListOfCloneable(this.dailySales),
        cloneListOfCloneable(this.ticketTemplateSales),
    ) as this;
  }

  isSame(other: MonthlySalesIncomeSummary): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.monthOfYear, this.monthOfYear) &&
      other.timezone === this.timezone &&
      other.currency === this.currency &&
      isSameList(other.dailySales, this.dailySales, isSameData) &&
      isSameList(other.ticketTemplateSales, this.ticketTemplateSales, isSameData);
  }

  addTicketOrder(order: EventTicketOrder) {
    if(order.status !== TicketOrderStatus.Open) {
      return;
    }

    const creationDate = order.creationDate;
    const day = DayOfYear.fromDate(creationDate);

    let dailySalesIndex: number | null = null;
    let dailySales: DailySalesIncomeSummary;

    for(let i = 0; i < this.dailySales.length; ++i) {
      if(this.dailySales[i].day.isSame(day)) {
        dailySalesIndex = i;
        break;
      }
    }

    if(isNotNullOrUndefined(dailySalesIndex)) {
      dailySales = this.dailySales[dailySalesIndex];
    }
    else {
      dailySales = DailySalesIncomeSummary.empty(this.currency, day);
      this.dailySales.push(dailySales);
    }

    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfTicketOrder(order);

    let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
    orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

    if(isNullOrUndefined(orderPrice)) {
      orderPrice ??= Price.zero(this.currency);

      for(const ticket of order.tickets) {
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        orderPrice = orderPrice?.add(ticketPrice);
      }
    }

    dailySales.addTicketOrder(order, discountDetails);

    for(let i = 0; i < order.tickets.length; ++i) {
      const ticket = order.tickets[i];
      if(ticket.status !== EventTicketStatus.Live && ticket.status !== EventTicketStatus.Scanned) {
        continue;
      }

      let templateSalesIndex: number | null = null;
      for(let j = 0; j < this.ticketTemplateSales.length; ++j) {
        if(this.ticketTemplateSales[j].templateId === ticket.templateId) {
          templateSalesIndex = j;
          break;
        }
      }

      let templateSales: TicketTemplateSalesIncomeSummary;
      if(isNotNullOrUndefined(templateSalesIndex)) {
        templateSales = this.ticketTemplateSales[templateSalesIndex];
      }
      else {
        templateSales = TicketTemplateSalesIncomeSummary.empty(this.currency, ticket.templateId);
        this.ticketTemplateSales.push(templateSales);
      }

      templateSales.addEventTicketOfTicketOrder(order, discountDetails, i);
    }
  }

  addOnlineTicketOrder(order: OnlineEventTicketOrder) {
    if(order.status !== TicketOrderStatus.Open) {
      return;
    }

    const creationDate = order.creationDate;
    const day = DayOfYear.fromDate(creationDate);

    let dailySalesIndex: number | null = null;
    let dailySales: DailySalesIncomeSummary;

    for(let i = 0; i < this.dailySales.length; ++i) {
      if(this.dailySales[i].day.isSame(day)) {
        dailySalesIndex = i;
        break;
      }
    }

    if(isNotNullOrUndefined(dailySalesIndex)) {
      dailySales = this.dailySales[dailySalesIndex];
    }
    else {
      dailySales = DailySalesIncomeSummary.empty(this.currency, day);
      this.dailySales.push(dailySales);
    }

    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfOnlineTicketOrder(order);

    let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
    orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

    if(isNullOrUndefined(orderPrice)) {
      orderPrice ??= Price.zero(this.currency);

      for(const ticket of order.tickets) {
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        orderPrice = orderPrice?.add(ticketPrice);
      }
    }

    dailySales.addOnlineTicketOrder(order, discountDetails);

    for(let j = 0; j < order.tickets.length; ++j) {
      const ticket = order.tickets[j];
      if(ticket.status !== EventTicketStatus.Live && ticket.status !== EventTicketStatus.Scanned) {
        continue;
      }

      let templateSalesIndex: number | null = null;
      for(let k = 0; k < this.ticketTemplateSales.length; ++k) {
        if(this.ticketTemplateSales[k].templateId === order.tickets[j].templateId) {
          templateSalesIndex = k;
          break;
        }
      }

      let templateSales: TicketTemplateSalesIncomeSummary;
      if(isNotNullOrUndefined(templateSalesIndex)) {
        templateSales = this.ticketTemplateSales[templateSalesIndex];
      }
      else {
        templateSales = TicketTemplateSalesIncomeSummary.empty(this.currency, order.tickets[j].templateId);
        this.ticketTemplateSales.push(templateSales);
      }

      templateSales.addEventTicketOfOnlineTicketOrder(order, discountDetails, j);
    }
  }

  removeTicketOrder(order: EventTicketOrder, originalOrder: EventTicketOrder) {
    if(order.status !== TicketOrderStatus.Canceled && order.status !== TicketOrderStatus.Refunded) {
      return;
    }

    const creationDate = order.creationDate;
    const day = DayOfYear.fromDate(creationDate);

    let dailySalesIndex: number | null = null;
    for(let i = 0; i < this.dailySales.length; ++i) {
      if(this.dailySales[i].day.isSame(day)) {
        dailySalesIndex = i;
        break;
      }
    }

    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfTicketOrder(order);

    if(isNotNullOrUndefined(dailySalesIndex)) {
      const dailySales = this.dailySales[dailySalesIndex];

      let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
      orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

      if(isNullOrUndefined(orderPrice)) {
        orderPrice ??= Price.zero(this.currency);

        for(const ticket of order.tickets) {
          const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
          orderPrice = orderPrice?.add(ticketPrice);
        }
      }

      dailySales.removeTicketOrder(order, originalOrder, discountDetails);
    }

    for(let i = 0; i < order.tickets.length; ++i) {
      let templateSalesIndex: number | null = null;
      for(let j = 0; j < this.ticketTemplateSales.length; ++j) {
        const ticket = order.tickets[i];

        if(this.ticketTemplateSales[j].templateId === ticket.templateId) {
          templateSalesIndex = j;
          break;
        }
      }

      let templateSales: TicketTemplateSalesIncomeSummary;
      if(isNotNullOrUndefined(templateSalesIndex)) {
        templateSales = this.ticketTemplateSales[templateSalesIndex];
        templateSales.removeEventTicketOfTicketOrder(order, discountDetails, i);
      }
    }
  }

  removeOnlineTicketOrder(order: OnlineEventTicketOrder) {
    if(order.status !== TicketOrderStatus.Canceled && order.status !== TicketOrderStatus.Refunded) {
      return;
    }

    const creationDate = order.creationDate;
    const day = DayOfYear.fromDate(creationDate);

    let dailySalesIndex: number | null = null;
    for(let i = 0; i < this.dailySales.length; ++i) {
      if(this.dailySales[i].day.isSame(day)) {
        dailySalesIndex = i;
        break;
      }
    }

    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfOnlineTicketOrder(order);

    if(isNotNullOrUndefined(dailySalesIndex)) {
      const dailySales = this.dailySales[dailySalesIndex];

      let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
      orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

      if(isNullOrUndefined(orderPrice)) {
        orderPrice ??= Price.zero(this.currency);

        for(const ticket of order.tickets) {
          const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
          orderPrice = orderPrice?.add(ticketPrice);
        }
      }

      dailySales.removeOnlineTicketOrder(order, discountDetails);
    }

    for(let j = 0; j < order.tickets.length; ++j) {
      let templateSalesIndex: number | null = null;
      for(let k = 0; k < this.ticketTemplateSales.length; ++k) {
        if(this.ticketTemplateSales[k].templateId === order.tickets[j].templateId) {
          templateSalesIndex = k;
          break;
        }
      }

      let templateSales: TicketTemplateSalesIncomeSummary;
      if(isNotNullOrUndefined(templateSalesIndex)) {
        templateSales = this.ticketTemplateSales[templateSalesIndex];
        templateSales.removeEventTicketOfOnlineTicketOrder(order, discountDetails, j);
      }
    }
  }

  removeEventTicket(order: EventTicketOrder, index: number) {
    const ticket = order.tickets[index];
    if(ticket.status !== EventTicketStatus.Canceled && ticket.status !== EventTicketStatus.Refunded) {
      return;
    }

    const creationDate = order.creationDate;
    const day = DayOfYear.fromDate(creationDate);

    let dailySalesIndex: number | null = null;
    for(let i = 0; i < this.dailySales.length; ++i) {
      if(this.dailySales[i].day.isSame(day)) {
        dailySalesIndex = i;
        break;
      }
    }

    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfTicketOrder(order);
    if(isNotNullOrUndefined(dailySalesIndex)) {
      const dailySales = this.dailySales[dailySalesIndex];
      let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
      orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

      if(isNullOrUndefined(orderPrice)) {
        orderPrice ??= Price.zero(this.currency);

        for(const ticket of order.tickets) {
          const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
          orderPrice = orderPrice?.add(ticketPrice);
        }
      }

      dailySales.removeEventTicket(order, discountDetails, index);
    }

    let templateSalesIndex: number | null = null;
    for(let i = 0; i < this.ticketTemplateSales.length; ++i) {
      if(this.ticketTemplateSales[i].templateId === order.tickets[index].templateId) {
        templateSalesIndex = i;
        break;
      }
    }

    if(isNotNullOrUndefined(templateSalesIndex)) {
      this.ticketTemplateSales[templateSalesIndex].removeEventTicketOfTicketOrder(order, discountDetails, index);
    }
  }
}

export class MonthlySalesIncomeSummaryBuilder extends DataModelBuilder<MonthlySalesIncomeSummary> {
  build(context: SerializationContext, map: SerializedData): MonthlySalesIncomeSummary {
    return MonthlySalesIncomeSummary.fromMap(context, map);
  }
}
